import PageMain from "@/components/page-main";
import { Tree, Popconfirm, Space, Tooltip, Modal, Form, Input, message } from 'antd';
import type { DataNode } from 'antd/es/tree';
import { DownOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import useFetch from "@/hooks/useFetch";
import Loading from "@/components/loading";
import { Access, useAccess } from 'umi';
import { useState } from "react";
import { request } from '@/utils/request';

export type Tag = { id: number; name: string; parentTag: number; definition: string; isLeaf: boolean; concatName: string; };

const Index: React.FC = () => {
  const { data, loading, fetchData } = useFetch("/local/knowledge/tag", {});
  const [visible, setVisible] = useState<boolean>(false);

  const [form] = Form.useForm();
  const [currentTag, setCurrentTag] = useState<Tag | null>(null);
  const [handleType, setHandleType] = useState<'edit' | 'add' | null>(null);

  const access = useAccess();

  const handle = (id: number, type: 'edit' | 'add') => {
    const tag = data.find((t: Tag) => t.id === id) ?? { id: null };
    setCurrentTag(tag);
    setHandleType(type);
    if (type === 'edit') form.setFieldsValue({
      name: tag?.name,
      definition: tag?.definition,
    });
    setVisible(true);
  }

  const clear = () => {
    setVisible(false);
    setCurrentTag(null);
    setHandleType(null);
    form.resetFields();
  }

  const handleOk = async () => {
    const { name, definition } = form.getFieldsValue();

    const { resp } = await request(`/local/knowledge/tag${handleType === 'edit' ? `/${currentTag?.id}` : ""}`, {
      method: handleType === "edit" ? "PUT" : "POST",
      data: {
        ...(handleType === 'add' ? { parentTag: currentTag?.id } : {}),
        name,
        definition,
      }
    });
    if (resp?.code !== 0) return message.error(resp?.message);
    message.success(resp?.message);
    fetchData();
    clear();
  }

  const handleDelete = async (id: number) => {
    const { resp } = await request(`/local/knowledge/tag/${id}`, { method: "DELETE" });
    if (resp?.code !== 0) return message.error(resp?.message);
    message.success(resp?.message);
    fetchData();
  }

  const treeData: DataNode[] = useMemo(() => {
    function findChildren(parentTag: number) {
      return data.filter((t: Tag) => t.parentTag === parentTag).map((vo: Tag) => {
        return {
          title: <Space>
            <Tooltip title={vo.definition}>{vo.name}</Tooltip>
            <Access accessible={access.tagEdit}>
              <a onClick={() => handle(vo.id, 'edit')}>编辑</a>
            </Access>
            <Access accessible={access.tagEdit}>
              <a onClick={() => handle(vo.id, 'add')}>新增</a>
            </Access>
            <Access accessible={access.tagDelete}>
              <Popconfirm title="是否确定删除该标签？" onConfirm={() => handleDelete(vo.id)}>
                <a>删除</a>
              </Popconfirm>
            </Access>
          </Space>,
          key: vo.id,
          ...vo,
          children: findChildren(vo.id)
        }
      })
    }

    const root = data.filter((tag: Tag) => !Boolean(tag.parentTag));

    const result = root.map((vo: Tag) => {
      return {
        ...vo,
        key: vo.id,
        title: <Space>
          <Tooltip title={vo.definition}>{vo.name}</Tooltip>
          <Access accessible={access.tagEdit}>
            <a onClick={() => handle(vo.id, 'edit')}>编辑</a>
          </Access>
          <Access accessible={access.tagEdit}>
            <a onClick={() => handle(vo.id, 'add')}>新增</a>
          </Access>
          <Access accessible={access.tagDelete}>
            <Popconfirm title="是否确定删除该标签？" onConfirm={() => handleDelete(vo.id)}>
              <a>删除</a>
            </Popconfirm>
          </Access>
        </Space>,
        children: findChildren(vo.id)
      }
    })

    return [{
      title: <Space>
        根
        <Access accessible={access.tagEdit}>
          <a onClick={() => handle(0, 'add')}>新增</a>
        </Access>
      </Space>,
      key: 0,
      children: result
    }];
  }, [data]);

  return <PageMain>
    <Loading loading={loading} />
    <Tree
      showLine
      switcherIcon={<DownOutlined />}
      defaultExpandedKeys={[0]}
      treeData={treeData}
    />

    <Modal
      visible={visible}
      title={`${handleType === 'add' ? '新增' : '编辑'}标签`}
      onCancel={clear}
      onOk={handleOk}
    >
      <Form form={form}>
        <Form.Item label="名称" name="name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="描述" name="definition">
          <Input />
        </Form.Item>
        {/* <Form.Item label="是否为叶子" valuePropName="checked" name="isLeaf" tooltip="即是否为最外层的标签。若是，则不能给其再创建子标签。产品问答的标签只能是叶子标签。">
          <Switch disabled={handleType === 'edit'} />
        </Form.Item> */}
      </Form>
    </Modal>
  </PageMain>
}

export default Index;
